import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { UploadSimple, FileCsv, ChartLine } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

export default function MetricsImport({ open, onOpenChange, onDone, campaignId }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const upload = async () => {
    if (!file) return;
    if (!campaignId) { toast.error("No campaign selected"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/campaigns/${campaignId}/creators/import-metrics`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      if (data.updated) {
        toast.success(`Metrics updated for ${data.updated} creator(s)`, { duration: 5000 });
      } else {
        toast.warning("No matching creators found", { duration: 5000 });
      }
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Import failed", { duration: 5000 });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) { setFile(null); setResult(null); } }}>
      <DialogContent data-testid="metrics-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2">
            <ChartLine size={22} weight="duotone" className="text-blue-600" />
            Import metrics from CSV
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            Update views, likes, comments and shares for many live creators at once. Recognized columns:{" "}
            <span className="font-mono text-xs">
              Creator Name, Views, Likes, Comments, Shares
            </span>
            . Creators are matched by <b>username</b> (case-insensitive) inside this campaign — no new rows are created.
          </p>

          <label className="flex flex-col items-center justify-center py-10 border-2 border-dashed border-slate-300 rounded-md cursor-pointer hover:border-blue-500 hover:bg-blue-50/40 transition-colors">
            <FileCsv size={32} className="text-slate-400" weight="duotone" />
            <div className="text-sm mt-2 text-slate-700 font-medium">
              {file ? file.name : "Click to choose a CSV file"}
            </div>
            <div className="text-xs text-slate-500 mt-1">Only .csv files</div>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="hidden"
              data-testid="metrics-file-input"
            />
          </label>

          {result && (
            <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200 text-sm text-emerald-800" data-testid="metrics-import-result">
              Updated <strong>{result.updated}</strong> creator(s).
              {result.not_found?.length > 0 && (
                <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                  <b>{result.not_found.length}</b> row(s) didn't match: <span className="font-mono">{result.not_found.slice(0, 5).join(", ")}{result.not_found.length > 5 ? "…" : ""}</span>
                </div>
              )}
              {result.errors?.length > 0 && (
                <div className="mt-1 text-xs text-rose-700">{result.errors.length} row error(s)</div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="metrics-cancel-btn">Close</Button>
          <Button onClick={upload} disabled={!file || busy} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="metrics-import-btn">
            <UploadSimple size={16} className="mr-1.5" />
            {busy ? "Importing…" : "Import metrics"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
